import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin } from "lucide-react";
import { fmtDateTime } from "../lib/time";
import { Stars, RiskBadge } from "./common";

export default function FeedbackCard({ f, onOpen, actions }) {
  const qa = (f.question_answers || []).filter((a) => a.value !== null && a.value !== undefined);
  const yes = qa.filter((a) => a.value).length;
  const dims = Object.keys(f.dimension_scores || {}).length;
  const lowDims = Object.entries(f.dimension_scores || {})
    .filter(([, v]) => typeof v === "number" && v < 3)
    .map(([k, v]) => ({ key: k, label: f.dim_labels?.[k] || String(k).replace(/_/g, " "), value: v }));
  const hasLowDim = lowDims.length > 0;
  const empName = (
    <span className="font-bold text-slate-800">{f.employee_name || "Removed employee"}</span>
  );
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(f.id)} onKeyDown={(e) => e.key === "Enter" && onOpen(f.id)}
      className={`group cursor-pointer rounded-xl border p-4 transition-colors ${hasLowDim ? "border-red-200 bg-red-50/60 hover:border-red-300 hover:bg-red-50" : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/20"}`} data-testid={`feedback-${f.id}`} data-low-dim={hasLowDim ? "true" : "false"}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <Stars value={f.rating} />
          <span className="font-head text-sm font-bold text-slate-900" data-testid={`rating-score-${f.id}`}>{Number(f.rating ?? 0).toFixed(1)}<span className="text-xs font-semibold text-slate-400"> / 5</span></span>
          {f.employee_id && f.employee_name ? (
            <Link
              to={`/app/employees/${f.employee_id}`}
              onClick={(e) => e.stopPropagation()}
              className="font-bold text-slate-800 underline-offset-2 hover:text-indigo-700 hover:underline"
              data-testid={`feedback-employee-link-${f.id}`}
            >
              {f.employee_name}
            </Link>
          ) : empName}
          {f.location_name && <span className="inline-flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{f.location_name}</span>}
          {f.point_name && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-bold text-violet-700" data-testid={`point-chip-${f.id}`}>{f.point_name}</span>}
          <RiskBadge status={f.review_status} testId={`risk-${f.id}`} />
          {f.has_dispute && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-600">Disputed</span>}
        </div>
        <span className="flex items-center gap-1 text-xs text-slate-400">{fmtDateTime(f.created_at, f.location_id)}<ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-500" /></span>
      </div>
      {f.comment && <p className="mt-2 text-sm text-slate-600">{f.comment}</p>}
      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
        {qa.length > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">{yes}/{qa.length} questions yes</span>}
        {dims > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">{dims} detail rating{dims === 1 ? "" : "s"}</span>}
        {hasLowDim && (
          <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700" data-testid={`low-dim-chip-${f.id}`}>
            Low: {lowDims.map((d) => `${d.label} ${d.value}★`).join(", ")}
          </span>
        )}
        {f.problem_category && <span className="rounded-full bg-rose-50 px-2 py-0.5 font-semibold text-rose-700">{f.problem_category}</span>}
        {f.trust_signals?.length > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">Signals: {f.trust_signals.join(", ")}</span>}
      </div>
      {actions && <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>{actions}</div>}
    </div>
  );
}
